import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import type { Project } from '../api/types';
import type { InsightDocument } from '../api/insights';

const projectLevelLabel = (l: Project['project_level']) => (l === 'Business_Unit' ? 'Business unit' : l);

// =============================================================================
// Home — Projects list
// =============================================================================
// Portfolio summary card removed 2026-05-19 per product direction. The
// /projects/executive-summary endpoint still exists on the backend but is no
// longer surfaced on the home page. If we want to revive a cross-project
// view later, add a dedicated /portfolio page rather than a card here.

/**
 * The latest completed run's headline (C-026): date, Act now count and Overall High or above.
 * Reads the same document as the project's Overview and Runs tabs (shared query key).
 */
function LastRunSummary({ projectId }: { projectId: string }) {
  const latest = useQuery({
    queryKey: ['project-insights', projectId],
    queryFn: () => api.get<InsightDocument>(`/projects/${projectId}/insights/latest`),
    retry: false,
  });
  const d = latest.data;
  if (latest.isLoading) return <div className="text-xs text-ink-300 mt-1">&nbsp;</div>;
  if (!d) return <div className="text-xs text-ink-500 mt-1" data-testid="project-last-run">No completed run yet</div>;
  const date = d.run_completed_at
    ? new Date(d.run_completed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;
  return (
    <div className="text-xs text-ink-500 mt-1" data-testid="project-last-run">
      Last run{date ? ` ${date}` : ''}: {d.kpis.priorities.P1} Act now · {d.kpis.overall_high_plus.count} of {d.kpis.overall_high_plus.of} High or above on Overall risk
    </div>
  );
}

export function Home() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['projects'],
    // The server's maximum page (200); the default of 50 hid later projects with no pager.
    queryFn: () => api.get<Project[]>('/projects?limit=200'),
  });

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <div className="flex items-baseline justify-between mb-6">
        <h1 className="text-3xl">Projects</h1>
        <Link to="/projects/new" className="btn-primary no-underline">
          New project
        </Link>
      </div>

      {isLoading && <p className="text-ink-500">Loading projects…</p>}
      {error && (
        <p className="text-accent">Failed to load projects. Is the backend running?</p>
      )}

      {data && data.length === 0 && (
        <div className="border border-dashed border-ink-200 rounded-sm py-16 px-6 text-center">
          <p className="font-display text-xl italic text-ink-500 mb-2">No projects yet.</p>
          <p className="text-sm text-ink-500 mb-4">Create your first quantum risk assessment.</p>
          <Link to="/projects/new" className="btn-primary no-underline">New project</Link>
        </div>
      )}

      {data && data.length > 0 && (
        <ul className="border border-ink-200 rounded-sm divide-y divide-ink-200 bg-white">
          {data.map((p) => (
            <li key={p.id}>
              <Link
                to={`/projects/${p.id}`}
                className="block px-4 py-3 hover:bg-ink-50 transition-colors"
              >
                <div className="flex items-baseline justify-between">
                  <span className="font-medium text-ink">{p.name}</span>
                  <span className="text-xs text-ink-300">
                    {/* Status cannot be edited, so only an archived project is flagged (C-027). */}
                    {projectLevelLabel(p.project_level)}{p.status === 'Archived' ? ' · Archived' : ''}
                  </span>
                </div>
                <LastRunSummary projectId={p.id} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}